/**
 * Navigation data — single source of truth for the desktop Services mega
 * menu and the mobile menu, in both languages.
 *
 * Every label and description here is pinned from approved on-site copy
 * (Services index cards, page SEOHead titles, Industries hub). Do not add
 * or reword entries without checking .claude/rules/copy.md first.
 */

export type MetroAccent = "red" | "orange" | "yellow" | "green" | "blue";

export interface MenuLink {
  label: string;
  to: string;
  description?: string;
}

export interface ServiceGroup {
  heading: string;
  accent: MetroAccent;
  links: MenuLink[];
}

export interface NavData {
  home: MenuLink;
  about: MenuLink;
  contact: MenuLink;
  services: {
    trigger: string;
    panelLabel: string;
    /** Route prefixes that mark the trigger as active. */
    activePrefixes: string[];
    groups: ServiceGroup[];
    viewAll: MenuLink;
    industries: {
      heading: string;
      accent: MetroAccent;
      links: MenuLink[];
      viewAll: MenuLink;
    };
    featured: {
      eyebrow: string;
      cta: MenuLink;
      secondary: MenuLink[];
    };
  };
}

export const navData: Record<"en" | "es", NavData> = {
  en: {
    home: { label: "Home", to: "/" },
    about: { label: "About", to: "/about" },
    contact: { label: "Contact", to: "/contact" },
    services: {
      trigger: "Services",
      panelLabel: "Services menu",
      activePrefixes: ["/services", "/industries"],
      viewAll: { label: "View all services", to: "/services" },
      groups: [
        {
          heading: "Build",
          accent: "orange",
          links: [
            {
              label: "Website Development",
              to: "/services/website-development",
              description: "Custom React websites on AWS",
            },
            {
              label: "Ecommerce",
              to: "/services/ecommerce-websites",
              description: "Stripe checkout + your own shipping rules",
            },
            {
              label: "Law Firm Websites",
              to: "/services/law-firm-websites",
              description: "Attorney websites built to be found",
            },
            {
              label: "Photography",
              to: "/services/photography",
              description: "Food, model & brand shoots",
            },
          ],
        },
        {
          heading: "Grow",
          accent: "green",
          links: [
            {
              label: "SEO",
              to: "/services/local-seo",
              description: "Thousands of pages published automatically",
            },
            {
              label: "Technical SEO",
              to: "/services/technical-seo",
              description: "Every service you sell, findable on Google",
            },
            {
              label: "Google Maps Optimization",
              to: "/services/google-business-profile",
              description: "Built to rank in Google's top local results",
            },
            {
              label: "Social Media Management",
              to: "/services/social-media-management",
              description: "Instagram + Facebook organic + Reels",
            },
            {
              label: "Growth Marketing",
              to: "/services/growth-marketing",
              description: "One growth specialist, four connected channels",
            },
            {
              label: "Digital Marketing",
              to: "/services/digital-marketing",
              description: "Site, SEO, Maps, email, and content under one roof",
            },
          ],
        },
        {
          heading: "Stream",
          accent: "blue",
          links: [
            {
              label: "Live Stream Production",
              to: "/services/live-stream-production",
              description: "Custom OBS overlays coded, not templated",
            },
            {
              label: "Remote Stream Production",
              to: "/services/remote-stream-production",
              description: "Live stream production, run remotely",
            },
            {
              label: "Chat Moderation",
              to: "/services/chat-moderation",
              description: "Live chat moderation for streamers",
            },
            {
              label: "Kick Streams",
              to: "/live",
              description: "Founder stories on Kick",
            },
          ],
        },
      ],
      industries: {
        heading: "Industries",
        accent: "yellow",
        links: [
          { label: "Restaurants", to: "/industries/restaurants" },
          { label: "Law Firms", to: "/industries/attorneys" },
          { label: "Ecommerce", to: "/industries/ecommerce" },
          { label: "Fashion Brands", to: "/industries/fashion-brands" },
          { label: "Salons & Beauty", to: "/industries/salons" },
        ],
        viewAll: { label: "All industries", to: "/industries" },
      },
      featured: {
        eyebrow: "Start here",
        cta: { label: "Set up a 15-min call", to: "/book" },
        secondary: [
          { label: "View all services", to: "/services" },
          { label: "Business Partners", to: "/business-partners" },
          { label: "Sample monthly report", to: "/sample-report" },
        ],
      },
    },
  },
  es: {
    home: { label: "Inicio", to: "/es" },
    about: { label: "Sobre Nosotros", to: "/es/sobre-nosotros" },
    contact: { label: "Contacto", to: "/es/contacto" },
    services: {
      trigger: "Servicios",
      panelLabel: "Menú de servicios",
      activePrefixes: ["/es/servicios", "/es/industrias"],
      viewAll: { label: "Ver todos los servicios", to: "/es/servicios" },
      groups: [
        {
          heading: "Construir",
          accent: "orange",
          links: [
            {
              label: "Desarrollo de Sitios Web",
              to: "/es/servicios/desarrollo-web",
              description: "Sitios React a la medida sobre AWS",
            },
            {
              label: "Ecommerce",
              to: "/es/servicios/sitios-web-ecommerce",
              description: "Checkout de Stripe y envío a la medida",
            },
            {
              label: "Sitios Web para Bufetes",
              to: "/es/servicios/sitios-web-bufetes",
              description: "Sitios para abogados, hechos para ser encontrados",
            },
            {
              label: "Fotografía",
              to: "/es/servicios/fotografia",
              description: "Comida, modelos y marca",
            },
          ],
        },
        {
          heading: "Crecer",
          accent: "green",
          links: [
            {
              label: "SEO",
              to: "/es/servicios/seo-local",
              description: "Miles de páginas publicadas automáticamente",
            },
            {
              label: "SEO Técnico",
              to: "/es/servicios/seo-tecnico",
              description: "Cada servicio que vendes, encontrable en Google",
            },
            {
              label: "Optimización de Google Maps",
              to: "/es/servicios/google-business-profile",
              description: "Posiciona en los resultados locales de Google",
            },
            {
              label: "Gestión de Redes Sociales",
              to: "/es/servicios/gestion-redes-sociales",
              description: "Instagram y Facebook orgánico + Reels",
            },
            {
              label: "Marketing de Crecimiento",
              to: "/es/servicios/marketing-de-crecimiento",
              description: "Un especialista, cuatro canales conectados",
            },
            {
              label: "Marketing Digital",
              to: "/es/servicios/marketing-digital",
              description: "Sitio, SEO, Maps, email y contenido bajo un solo techo",
            },
          ],
        },
        {
          heading: "Transmitir",
          accent: "blue",
          links: [
            {
              label: "Transmisiones en Vivo",
              to: "/es/servicios/transmisiones-en-vivo",
              description: "Superposiciones OBS programadas, no plantillas",
            },
            {
              label: "Producción Remota",
              to: "/es/servicios/produccion-remota",
              description: "Producción de transmisiones, en remoto",
            },
            {
              label: "Moderación de Chat",
              to: "/es/servicios/moderacion-de-chat",
              description: "Moderación de chat para transmisiones",
            },
            {
              label: "Streams en Kick",
              to: "/es/en-vivo",
              description: "Historias de fundadores en Kick",
            },
          ],
        },
      ],
      industries: {
        heading: "Industrias",
        accent: "yellow",
        links: [
          { label: "Restaurantes", to: "/es/industrias/restaurantes" },
          { label: "Bufetes de Abogados", to: "/es/industrias/abogados" },
          { label: "Ecommerce", to: "/es/industrias/ecommerce" },
          { label: "Marcas de Moda", to: "/es/industrias/marcas-de-moda" },
          { label: "Salones de Belleza", to: "/es/industrias/salones" },
        ],
        viewAll: { label: "Todas las industrias", to: "/es/industrias" },
      },
      featured: {
        eyebrow: "Empieza aquí",
        cta: { label: "Agendar llamada de 15 min", to: "/es/agendar" },
        secondary: [
          { label: "Ver todos los servicios", to: "/es/servicios" },
          { label: "Socios de Negocio", to: "/es/socios-de-negocio" },
          { label: "Reporte mensual de muestra", to: "/es/reporte-de-muestra" },
        ],
      },
    },
  },
};
